import _get from 'lodash/get';
import _set from 'lodash/set';
import { i18n as I18n } from 'i18next';
import { useFormikContext } from 'formik';
import { ApiValidationFailedException } from '@app/api';
import { useTranslation } from 'react-i18next';
import { ValidationError } from 'yup';
import { ChangeEvent, ChangeEventHandler, useCallback } from 'react';

interface FinalProps<TValue = string | number | boolean> {
  name?: string;
  validate?: (value: any) => string | Promise<void> | undefined;
  error?: string;
  value?: TValue;
  touched?: boolean;
  onChangeEvent?: (e: React.ChangeEvent<any>) => any;
}

export function useOptionalField<TValue = string | number | boolean>(
  props: FinalProps<TValue>,
) {
  const { name } = props;
  const formik = useFormikContext();
  if (!formik || !name) {
    return {
      field: {
        onChange: undefined,
        onBlur: undefined,
        value: props.value !== undefined ? props.value : undefined,
      },
      meta: null,
      helpers: null,
    };
  }

  const fieldProps = formik.getFieldProps({ ...props, name });
  const onChange = (e: React.ChangeEvent<any>) => {
    clearError(formik.status, name);
    if (props.onChangeEvent) {
      props.onChangeEvent(e);
    }

    return fieldProps.onChange(e);
  };

  return {
    field: {
      ...fieldProps,
      onChange,
    },
    meta: formik.getFieldMeta<TValue>(name),
    helpers: formik.getFieldHelpers(name),
  };
}

export interface ControlledFieldProps<TValue, TOnChange extends Function> {
  name?: string;
  onChange?: TOnChange;
  value?: TValue | undefined;
  onValueChange?: (value?: TValue, name?: string) => any;
}

export function useControlledField<
  TValue = string,
  TOnChange extends (
    ...params: any[]
  ) => any = ChangeEventHandler<HTMLInputElement>,
>(props: ControlledFieldProps<TValue, TOnChange>) {
  const { name, onValueChange } = props;
  const {
    field: { value, onChange: fOnChange },
    helpers,
  } = useOptionalField(props);

  const pOnChange = props.onChange;

  const onChange = useCallback(
    (...params: any[]): any => {
      if (pOnChange) {
        return pOnChange(...params);
      }
    },
    [pOnChange],
  );

  const setFieldValue = helpers?.setValue;
  const setTouched = helpers?.setTouched;
  const setValue = useCallback(
    async (val: TValue | undefined): Promise<any> => {
      if (onValueChange) {
        onValueChange(val, name);
      }
      if (setFieldValue) {
        await setFieldValue(val, true);
      }
      if (setTouched) {
        await setTouched(true);
      }
    },
    [onValueChange, setFieldValue, name, setTouched],
  );

  const onFieldChange = useCallback(
    (e: ChangeEvent<any>) => {
      if (pOnChange) {
        return pOnChange(e);
      }
      const val = e.currentTarget.value;
      if (onValueChange) {
        onValueChange(val, name);
      }
      if (fOnChange) {
        fOnChange(e);
      }
    },
    [pOnChange, onValueChange, fOnChange],
  );

  return {
    onChange: onChange as TOnChange,
    setValue,
    name,
    value,
    onFieldChange,
  };
}

const getErrorTranslation = (i18n: I18n, error?: ValidationError | string) => {
  if (!error) {
    return undefined;
  }
  if (typeof error === 'string') {
    return i18n.t(error);
  }
  const { name, type, ...yupParams } = error;
  let errorName = (error as any).errorName || type;
  if (i18n.exists(`form.errors.${name}.${errorName}`)) {
    return i18n.t(`form.errors.${name}.${errorName}`, yupParams);
  }
  if (i18n.exists(`form.errors.${name}.default`)) {
    return i18n.t(`form.errors.${name}.default`, yupParams);
  }
  if (i18n.exists(`form.errors.${errorName}`)) {
    return i18n.t(`form.errors.${errorName}`, yupParams);
  }
  return i18n.t(`form.errors.invalid`, yupParams);
};

export const useFormikError = (props: FinalProps) => {
  const { name } = props;
  const formik = useFormikContext();
  const { i18n } = useTranslation('common');

  if (props.error) {
    return props.error;
  }

  if (!formik || !name) {
    return '';
  }

  const meta = formik.getFieldMeta(name);

  const error: string | undefined =
    meta.touched || props.touched ? meta.error : undefined;

  // dirty fix when the element is in an array and the error is at the array level
  if (/.+\[[0-9]*\]$/.test(name) && meta.error && meta.error.length === 1) {
    return '';
  }

  if (error) {
    return getErrorTranslation(i18n, error);
  }
  if (formik.status && formik.status instanceof ApiValidationFailedException) {
    const formikErrors = formik.status.getFormikErrors();

    const formikError = _get(formikErrors, name);
    if (typeof formikError === 'string') {
      return formikError;
    }
  } else if (formik.status) {
    return _get(formik.status, name);
  }
};

export const clearError = (status: any, name: string) => {
  if (status && status instanceof ApiValidationFailedException) {
    status.removeError(name);
  } else if (status) {
    return _set(status, name, null);
  }
};
